import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, KeyRound, Plus, ShieldAlert } from "lucide-react";
import { toast } from "../lib/toast";
import type { ApiKeyScope } from "@smsbridge/shared";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { DatePicker } from "../components/ui/DatePicker";
import { Highlight } from "../components/ui/Highlight";
import { PageHeader } from "../components/ui/PageHeader";
import { Pagination } from "../components/ui/Pagination";
import { SearchInput } from "../components/ui/SearchInput";
import { Toolbar, ToolbarGrow } from "../components/ui/Toolbar";
import { RowAction, RowActions } from "../components/ui/RowAction";
import { StatusPill } from "../components/ui/StatusPill";
import { Table, type TableColumn } from "../components/ui/Table";
import { api, apiErrorMessage } from "../lib/api";
import { formatDate, formatRelative } from "../lib/format";
import { useClientTable } from "../lib/useClientTable";
import { useMinimumDuration } from "../lib/useMinimumDuration";

interface ApiKeyRow {
  _id: string;
  name: string;
  prefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

const ALL_SCOPES: Array<{ value: ApiKeyScope; label: string; help: string }> = [
  { value: "sms:send", label: "sms:send", help: "Send messages and retry or cancel them." },
  { value: "sms:read", label: "sms:read", help: "Read message history and batch progress." },
  { value: "devices:read", label: "devices:read", help: "List paired devices and their status." },
  { value: "devices:manage", label: "devices:manage", help: "Rename, disable or remove devices." },
  { value: "webhooks:manage", label: "webhooks:manage", help: "Create and delete webhooks." },
  { value: "contacts:manage", label: "contacts:manage", help: "Create and update contacts." },
];

export function ApiKeysPage() {
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<ApiKeyScope[]>(["sms:send", "sms:read"]);
  const [expiresAt, setExpiresAt] = useState("");
  const [revealed, setRevealed] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyRow | null>(null);

  const keysQuery = useQuery({
    queryKey: ["api-keys"],
    queryFn: async () => (await api.get<ApiKeyRow[]>("/api-keys")).data,
  });

  const keysQueryLoading = useMinimumDuration(keysQuery.isLoading);

  const createMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ secret: string }>("/api-keys", {
          name,
          scopes,
          ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
        })
      ).data,
    onSuccess: (result) => {
      setCreateOpen(false);
      // The server will never return this value again — it exists only in this response.
      setRevealed(result.secret);
      setName("");
      setExpiresAt("");
      void queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      void queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not create the key.")),
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/api-keys/${id}`),
    onSuccess: () => {
      toast.success("Key revoked.");
      setRevokeTarget(null);
      void queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      void queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not revoke the key.")),
  });

  async function copySecret() {
    if (!revealed) return;
    await navigator.clipboard.writeText(revealed);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function toggleScope(scope: ApiKeyScope) {
    setScopes((current) =>
      current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope],
    );
  }

  function statusOf(row: ApiKeyRow) {
    if (row.revokedAt) return <StatusPill status="REVOKED" />;
    if (row.expiresAt && new Date(row.expiresAt) < new Date()) return <StatusPill status="EXPIRED" />;
    return <StatusPill status="ACTIVE" />;
  }

  // Client-side: GET /api-keys takes no query parameters and returns every key.
  const table = useClientTable<ApiKeyRow>(keysQuery.data, (row) => [
    row.name,
    row.prefix,
    ...row.scopes,
  ]);

  const columns: TableColumn<ApiKeyRow>[] = [
    {
      key: "name",
      header: "Name",
      render: (row) => (
        <div className="flex flex-col">
          <Highlight text={row.name} query={table.search} className="font-medium text-text" />
          <code className="text-xs text-text-muted">
            <Highlight text={row.prefix} query={table.search} />••••••••••••
          </code>
        </div>
      ),
    },
    {
      key: "scopes",
      header: "Scopes",
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.scopes.map((scope) => (
            <Badge key={scope} tone="neutral" className="font-mono text-[11px]">
              {scope}
            </Badge>
          ))}
        </div>
      ),
    },
    { key: "status", header: "Status", width: "110px", render: statusOf },
    {
      key: "lastUsedAt",
      header: "Last used",
      width: "130px",
      render: (row) => <span className="text-text-muted">{formatRelative(row.lastUsedAt)}</span>,
    },
    {
      key: "expiresAt",
      header: "Expires",
      width: "130px",
      render: (row) => (
        <span className="text-text-muted">{row.expiresAt ? formatDate(row.expiresAt) : "Never"}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "100px",
      align: "right",
      render: (row) =>
        row.revokedAt ? null : (
          <RowActions>
            <RowAction tone="danger" onClick={() => setRevokeTarget(row)}>
              Revoke
            </RowAction>
          </RowActions>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        sticky
        title="API Keys"
        description="Authenticate the public REST API with the x-api-key header. Scopes limit what each key may do."
        actions={
          <Button
            leftIcon={<Plus className="size-4" aria-hidden />}
            onClick={() => setCreateOpen(true)}
          >
            Create key
          </Button>
        }
      />

      {keysQuery.isError && (
        <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
          {apiErrorMessage(keysQuery.error, "Could not load your API keys.")}
        </p>
      )}

      <Toolbar sticky>
        <ToolbarGrow className="sm:max-w-xs">
          <SearchInput
            value={table.search}
            onChange={table.setSearch}
            placeholder="Search name, prefix or scope…"
          />
        </ToolbarGrow>
      </Toolbar>
      <Table
        stickyFirstColumn
        stickyHeader
        columns={columns}
        data={table.rows}
        getRowId={(row) => row._id}
        loading={keysQueryLoading}
        emptyState={
          table.search ? (
            <EmptyState
              icon={<KeyRound className="size-6" aria-hidden />}
              title={`No results for "${table.search}"`}
              description="Try a different name or scope, or clear the search to see every key."
            />
          ) : (
            <EmptyState
              icon={<KeyRound className="size-6" aria-hidden />}
              title="No API keys yet"
              description="Create one to send SMS from your own server or scripts."
              action={<Button onClick={() => setCreateOpen(true)}>Create key</Button>}
            />
          )
        }
      />

      <Pagination
        mode="counted"
        page={table.page}
        pageSize={table.pageSize}
        total={table.total}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
      />

      {/* Create */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create an API key"
        description="Give it a name you'll recognise later, and only the scopes it actually needs."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={createMutation.isPending}
              disabled={name.trim() === "" || scopes.length === 0}
              onClick={() => createMutation.mutate()}
            >
              Create key
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <Input
            label="Name"
            placeholder="Production server"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium text-text">Scopes</legend>
            {ALL_SCOPES.map((scope) => (
              <label
                key={scope.value}
                className="flex cursor-pointer items-start gap-2.5 rounded-control px-2 py-1.5 hover:bg-surface-hover"
              >
                <input
                  type="checkbox"
                  checked={scopes.includes(scope.value)}
                  onChange={() => toggleScope(scope.value)}
                  className="mt-0.5 size-4 accent-accent"
                />
                <span className="flex flex-col">
                  <code className="text-sm text-text">{scope.label}</code>
                  <span className="text-xs text-text-muted">{scope.help}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <DatePicker
            id="key-expiry"
            label="Expires (optional)"
            placeholder="Never expires"
            value={expiresAt}
            onChange={setExpiresAt}
            helperText="Leave blank for a key that never expires."
          />

        </div>
      </Modal>

      {/* Reveal — the only time this value is ever shown */}
      <Modal
        open={revealed !== null}
        onClose={() => setRevealed(null)}
        title="Copy your key now"
        description="This is the only time it will be shown. Only a hash is stored, so it cannot be recovered."
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 rounded-control bg-warn-soft px-3 py-3">
            <ShieldAlert className="mt-0.5 size-5 shrink-0 text-warn" aria-hidden />
            <p className="text-sm text-text">
              If you lose it you will have to revoke this key and create a new one.
            </p>
          </div>

          <code className="block break-all rounded-control border border-border bg-bg px-3 py-3 text-sm text-text">
            {revealed}
          </code>

          <Button
            className="w-full"
            leftIcon={
              copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />
            }
            onClick={() => void copySecret()}
          >
            {copied ? "Copied" : "Copy to clipboard"}
          </Button>

          <Button variant="secondary" className="w-full" onClick={() => setRevealed(null)}>
            I've saved it
          </Button>
        </div>
      </Modal>

      {/* Revoke confirm */}
      <Modal
        open={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        title={`Revoke "${revokeTarget?.name ?? ""}"?`}
        description="Any server still using this key will start getting 401s immediately. This cannot be undone."
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRevokeTarget(null)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              loading={revokeMutation.isPending}
              onClick={() => revokeTarget && revokeMutation.mutate(revokeTarget._id)}
            >
              Revoke key
            </Button>
          </div>
        }
      />
    </div>
  );
}
